"use client";

import React from "react";
import { Stethoscope, Scissors, Syringe, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const solutions = [{
  title: "Wellness Exams",
  description: "Comprehensive health checks designed to keep your pets thriving. We focus on preventative care and early detection to ensure a long, happy life for your companions.",
  icon: Stethoscope,
  isPopular: true,
  link: "/ServicesPage"
}, {
  title: "Advanced Surgery",
  description: "State-of-the-art surgical procedures performed with precision and compassion. Our expert team ensures the highest safety standards for every procedure.",
  icon: Scissors,
  isPopular: false,
  link: "/ServicesPage"
}, {
  title: "Vaccinations",
  description: "Essential protection against common diseases. We provide tailored immunization schedules to keep your pets safe and healthy in their environment.",
  icon: Syringe,
  isPopular: false,
  link: "/ServicesPage"
}];
export default function OurMedicalExpertise() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="265075" id="our-medical-expertise" className="bg-background text-foreground py-24 lg:py-32" ref={ref}>
      <div className="container mx-auto px-4 md:px-6">
        <div className={cn("mb-16 flex flex-col items-center text-center transition-all duration-700", isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <h2 className="font-default text-3xl font-semibold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Our Medical Expertise
          </h2>
          <p className="mt-4 max-w-[700px] text-lg text-muted-foreground font-default">
            Compassionate veterinary care delivered with clinical excellence and a gentle touch for every member of your family.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-12">
          {solutions.map((solution, i) => {
          const Icon = solution.icon;
          return <div key={i} data-index={i} className={cn("transition-all duration-700", isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0")} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Card className={cn("group relative flex h-full flex-col overflow-hidden border transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-primary/5 rounded-3xl", solution.isPopular ? "border-primary/20 shadow-lg md:-translate-y-4" : "border-border shadow-md")}>
                  {solution.isPopular && <div className="absolute right-4 top-4 z-10">
                      <Badge variant="default" className="bg-primary text-primary-foreground shadow-sm rounded-lg">
                        Recommended
                      </Badge>
                    </div>}
                  
                  <CardHeader className="pb-4 pt-8">
                    <div className="mb-6 inline-flex size-16 items-center justify-center rounded-lg bg-primary/5 text-primary transition-colors duration-500 group-hover:bg-primary/10">
                      <div className="relative">
                        <Icon className="size-8" strokeWidth={1.5} />
                        <div className="absolute -bottom-1 -right-1 size-3 rounded-lg bg-secondary shadow-sm" />
                      </div>
                    </div>
                    <CardTitle className="font-default text-xl font-semibold text-foreground">
                      {solution.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="flex flex-1 flex-col justify-between pb-8">
                    <div className="relative">
                      <p className="text-muted-foreground font-default leading-relaxed transition-all duration-500 group-hover:-translate-y-1">
                        {solution.description}
                      </p>
                      <div className="grid grid-rows-[0fr] transition-all duration-500 ease-in-out group-hover:grid-rows-[1fr]">
                        <div className="overflow-hidden">
                          <div className="pt-4 opacity-0 transition-opacity duration-500 delay-100 group-hover:opacity-100">
                            <Link to={solution.link} className="inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80">
                              Learn More
                              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}